import React, { useEffect, useState } from "react";
import FadeIn from "react-fade-in";
import { HiCheck } from "react-icons/hi";
import { useSegmentCallbackV2 } from "../../../src/segment";
import { EnvData, Fact, Variable, VariableFact } from "../../../src/types";
import { Badge } from "./Badges";
import Button from "../../Button";
import FactView from "../../FactView";
import LoadingPage from "../../LoadingPage";
import Modal from "../../Modal";
import Spinner from "../../Spinner";
import WithIcon from "../../WithIcon";
import { LogItem } from "../EnforcePage.utils";

interface ExplainDetailsProps {
  selectedEnv: EnvData;
  log: LogItem | null;
  isOpen: boolean;
  onClose: () => void;
}

// TODO these are shared with the /logs page- DRY these up at some point
const variableToTypedId = (variable: Variable) => {
  switch (variable.kind) {
    case "TypedId":
      return variable.value;
    case "TypedVariable":
      return { type: variable.value.type, id: "_" };
    case "FreeVariable":
      return { type: "_", id: "_" };
  }
};

function toFact(variableFact: VariableFact): Fact {
  return {
    predicate: variableFact.predicate,
    args: variableFact.args.map(variableToTypedId),
  };
}

export const ExplainDetails: React.FC<ExplainDetailsProps> = ({
  selectedEnv,
  log,
  isOpen,
  onClose,
}) => {
  const segmentCallback = useSegmentCallbackV2();

  interface Explanation {
    explanation: {
      solutions: {
        matching_fact_sets: {
          matching_facts: Fact[];
        }[];
      }[];
    };
  }

  const [solutions, setSolutions] = useState<Fact[][] | null>(null);

  useEffect(() => {
    if (!log) return;
    (async () => {
      try {
        const r = await fetch(
          `/web/environment/${selectedEnv.pub_id}/api/authorize/`,
          {
            method: "POST",
            body: JSON.stringify({
              explain: true,
              actor_type:
                log.fact.args[0].kind === "TypedId" &&
                log.fact.args[0].value.type,
              actor_id:
                log.fact.args[0].kind === "TypedId" &&
                log.fact.args[0].value.id,
              action:
                log.fact.args[1].kind === "TypedId" &&
                log.fact.args[1].value.id,
              resource_type:
                log.fact.args[2].kind === "TypedId" &&
                log.fact.args[2].value.type,
              resource_id:
                log.fact.args[2].kind === "TypedId" &&
                log.fact.args[2].value.id,
            }),
          }
        );

        const json: Explanation = await r.json();
        if (json && json.explanation && json.explanation) {
          let solutions: Fact[][] = json.explanation.solutions.reduce(
            (accumulator: Fact[][], s) => {
              return s.matching_fact_sets.reduce((acc: Fact[][], f) => {
                acc.push(f.matching_facts);
                return acc;
              }, accumulator);
            },
            []
          );

          setSolutions(solutions);
        }
      } catch (e) {
        console.error(`An unexpected error occurred: ${e}.`);
      }
    })();
  }, [selectedEnv, log]);

  const argParams = log?.fact.args
    .map((arg) => {
      const param =
        arg.kind === "TypedId" ? `${arg.value.type}:${arg.value.id}` : "";
      return `args=${encodeURIComponent(param)}`;
    })
    .join("&");
  const explainLink = `/explain?env=${selectedEnv.pub_id}&${argParams}`;

  return (
    <Modal
      isOpen={isOpen}
      showCloseButton={true}
      onClose={() => {
        onClose();
        setSolutions(null);
      }}
    >
      {!log ? (
        <LoadingPage />
      ) : (
        <>
          <div className="text-2xl font-space">Query Details</div>
          <div className="mt-6" />
          <div className="flex justify-between items-center">
            <div>
              <FactView fact={toFact(log.fact)} />
            </div>
            <div>
              {log.result === "allowed" ? (
                <span className="bg-green-700 text-white px-2 py-1 rounded font-bold text-sm uppercase">
                  Allowed
                </span>
              ) : (
                <span className="bg-red-700 text-white px-2 py-1 rounded font-bold text-sm uppercase">
                  Denied
                </span>
              )}
            </div>
          </div>
          <div className="mt-4 border-b" />
          {solutions === null ? (
            <Spinner />
          ) : (
            <div className="space-y-4 mt-4">
              {log.result === "allowed" ? (
                <>
                  <div>
                    The fact required to grant the{" "}
                    <Badge>
                      {log.fact.args[1].kind === "TypedId" &&
                        log.fact.args[1].value.id}
                    </Badge>{" "}
                    permission to{" "}
                    <Badge>
                      {log.fact.args[0].kind === "TypedId" &&
                        `${log.fact.args[0].value.type}:${log.fact.args[0].value.id}`}
                    </Badge>{" "}
                    for{" "}
                    <Badge>
                      {log.fact.args[2].kind === "TypedId" &&
                        `${log.fact.args[2].value.type}:${log.fact.args[2].value.id}`}
                    </Badge>{" "}
                    was found:
                  </div>
                  {solutions.map((s, idx) => {
                    return (
                      <FadeIn key={`${log.requestId}-solution-${idx}`}>
                        <div className="border border-gray-400 rounded shadow-md text-sm p-2 space-y-2">
                          {s.map((f, fidx) => {
                            return (
                              <WithIcon
                                top
                                icon={<HiCheck />}
                                key={`${log.requestId}-solution-${idx}-${fidx}`}
                              >
                                <FactView fact={f} />
                              </WithIcon>
                            );
                          })}
                        </div>
                      </FadeIn>
                    );
                  })}
                </>
              ) : (
                <>
                  <div>
                    The fact required to grant the{" "}
                    <Badge>
                      {log.fact.args[1].kind === "TypedId" &&
                        log.fact.args[1].value.id}
                    </Badge>{" "}
                    permission to{" "}
                    <Badge>
                      {log.fact.args[0].kind === "TypedId" &&
                        `${log.fact.args[0].value.type}:${log.fact.args[0].value.id}`}
                    </Badge>{" "}
                    for{" "}
                    <Badge>
                      {log.fact.args[2].kind === "TypedId" &&
                        `${log.fact.args[2].value.type}:${log.fact.args[2].value.id}`}
                    </Badge>{" "}
                    was not found. For a more detailed explanation, click below.
                  </div>
                  <div>
                    <a
                      href={explainLink}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => {
                        segmentCallback(
                          "Get Started - Enforcement - Explain - Denied link"
                        );
                      }}
                    >
                      <Button theme="default" medium>
                        Explain
                      </Button>
                    </a>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </Modal>
  );
};
